
import { Component, Vue } from 'vue-property-decorator';
import {
  ChatInterface,
  chatRateMap,
  ChatWSMessageInterface,
  MessageRecipientsType,
} from '@/services/QA/chat/interfaces';
import Themeable from 'vuetify/lib/mixins/themeable';
import ChatMessageImageDialog from '@/components/dialogs/qa/chat/ChatMessageImageDialog.vue';
import EscalateDialog from '@/components/dialogs/qa/escalation/EscalateDialog.vue';
import FeedbackRepositoryInterface, {
  FeedbackResponseInterface,
  FeedbackStatus,
} from '@/services/QA/feedback/interfaces';
import CommentRepositoryInterface, {
  ChatCommentInterface,
  CommentInterface,
} from '@/services/QA/comment/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import DeleteCommentDialog from '@/components/dialogs/qa/comment/DeleteCommentDialog.vue';
import { Roles } from '@/services/Roles';
import DeleteNoteDialog from '@/components/dialogs/qa/note/DeleteNoteDialog.vue';
import EditCommentDialog from '@/components/dialogs/qa/comment/EditCommentDialog.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ChatMessageImageDialog,
    EscalateDialog,
    DeleteCommentDialog,
    DeleteNoteDialog,
    EditCommentDialog,
  },
  inject: [
    'feedbackRepository',
    'userRepository',
    'commentRepository',
    'profileRepository',
  ],
  sockets: {
    chatData(msg: ChatWSMessageInterface): void {
      // @ts-ignore
      this.mergeChatProperties(msg.chat);
    },
    newChatComment(msg: { chatComment: CommentInterface }): void {
      this.$emit('updateChatComments', msg.chatComment);
    },
    updatedComment(msg: { comment: CommentInterface }): void {
      this.$emit('updatedCommentMessage', msg.comment);
    },
  },
})
export default class ChatFeedback extends Vue {
  private feedback!: FeedbackResponseInterface;
  private chat!: ChatInterface;
  private feedbackRepository!: FeedbackRepositoryInterface;
  private commentRepository!: CommentRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};
  private loading: boolean = true;
  private comment: string = '';
  private height: number = 0;
  private userRoles: object = Roles;
  private chatRateMap: { '0': string, '1': string, '2': string } = chatRateMap;

  private urlForDialog: string | false = false;
  private imageDialog: string = '';
  private escalateDialog: string | false = false;
  private chatData!: ChatInterface;
  private commentIdForDialog: number = 0;
  private deleteCommentDialog: string | false = false;
  private noteIdForDialog: number = 0;
  private deleteNoteDialog: string | false = false;
  private commentIdForEditDialog: number = 0;
  private commentContentForDialog: string = '';
  private editCommentDialog: string | false = false;

  public async beforeCreate(): Promise<void> {
    this.$on('updateChatComments', async (chatComment: ChatCommentInterface) => {
      if (chatComment.chatId === this.chat.id) {
        if (!this.usersMap[chatComment.comment.authorId]) {
          const user = await this.userRepository.getUsersByIds([chatComment.comment.authorId]);
          this.usersMap[user[0].id] = user[0];
        }
        this.chat.comments.push(chatComment);
        this.chat.comments = this.chat.comments.sort(
          (commentA, commentB) => commentA.comment.timestamp - commentB.comment.timestamp,
        );
      }
      this.$forceUpdate();
    });

    this.$on('updatedCommentMessage', (comment: CommentInterface) => {
      const commentIndex = this.chat.comments.findIndex((chatComment) => chatComment.comment.id === comment.id);

      if (commentIndex !== -1) {
        this.chat.comments[commentIndex].comment = comment;
      }

      this.$forceUpdate();
    });
  }

  public async created(): Promise<void> {
    await this.initialize();
    // @ts-ignore
    this.height = this.$parent.$parent.$el.offsetHeight;
  }

  public async initialize(): Promise<void> {
    try {
      this.feedback = await this.feedbackRepository.getOneById(Number(this.$route.params.feedbackId));
      this.chat = this.feedback.chat;
      if (this.chat.scores) {
        this.chat.scores = this.chat.scores.filter((chatScore) => !chatScore.score.deletedAt);
      }

      await this.getUsersInfo();

      if (this.chatData) {
        await this.mergeChatProperties(this.chatData);
      }


      if (this.feedback.feedback.status === FeedbackStatus.SENT
        && this.$store.getters.id === this.feedback.feedback.feedbackToUserId) {
        await this.feedbackRepository.changeFeedbackStatus(this.feedback.feedback.id, FeedbackStatus.SEEN);
        this.feedback.feedback.status = FeedbackStatus.SEEN;
      }
    } catch (e) {
      console.log(e);
      this.$errorDialog.show(e.response?.data?.message);
    }
  }

  private async addComment(): Promise<void> {
    try {
      const comment: Partial<CommentInterface> = {
        content: this.comment,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      await this.commentRepository.addChatComment(this.chat.id, comment, true);
      this.comment = '';

      if (this.feedback.feedback.status === FeedbackStatus.SEEN
        && this.feedback.feedback.feedbackToUserId === this.$store.getters.id) {
        await this.feedbackRepository.changeFeedbackStatus(this.feedback.feedback.id, FeedbackStatus.REPLIED);
        this.feedback.feedback.status = FeedbackStatus.REPLIED;
      }

      if (this.feedback.feedback.status === FeedbackStatus.REPLIED
        && this.feedback.feedback.feedbackFromUserId === this.$store.getters.id) {
        await this.feedbackRepository.changeFeedbackStatus(this.feedback.feedback.id, FeedbackStatus.ANSWERED);
        this.feedback.feedback.status = FeedbackStatus.ANSWERED;
      }
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async prepareCommentForEdit(commentId: number): Promise<void> {
    const commentIndex = this.chat.comments.findIndex((comment) => comment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.commentIdForEditDialog = this.chat.comments[commentIndex].comment.id;
      this.commentContentForDialog = this.chat.comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    }
  }

  private async editComment(commentId: number, content: string): Promise<void> {
    const commentIndex = this.chat.comments.findIndex((comment) => comment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.chat.comments[commentIndex].comment.content = content;
    }
  }


  private async closeReview(): Promise<void> {
    await this.$router.back();
  }

  private async closeFeedback(): Promise<void> {
    if (this.feedback.feedback.status === FeedbackStatus.CLOSED) {
      return;
    }

    await this.feedbackRepository.changeFeedbackStatus(this.feedback.feedback.id, FeedbackStatus.CLOSED);
    this.feedback.feedback.status = FeedbackStatus.CLOSED;
    this.$forceUpdate();
  }

  private showImage(url: string): void {
    this.imageDialog = 'chat-message-image-dialog';
    this.urlForDialog = url;
  }

  private async mergeChatProperties(chatData: ChatInterface): Promise<void> {
    if (!this.chat) {
      this.chatData = chatData;
      return;
    }

    if (this.chat.id !== chatData.id) {
      delete this.chatData;
      return;
    }

    this.chat.startedTimestamp = chatData.startedTimestamp;
    this.chat.endedTimestamp = chatData.endedTimestamp;
    this.chat.visitor = chatData.visitor;
    this.chat.agents = chatData.agents;
    this.chat.supervisors = chatData.supervisors;
    this.chat.rate = chatData.rate;
    this.chat.messages.map((message) => {
      const index = chatData.messages?.findIndex((msg) => {
        return msg.id === message.id;
      });

      message.comments = message.comments.sort(
        (commentA, commentB) => commentA.comment.timestamp - commentB.comment.timestamp,
      );
      return Object.assign(message, chatData.messages[index]);
    });

    this.chat.messages.map((message) => {
      message.isWhisper = message.recipients === MessageRecipientsType.AGENTS;

      return message;
    });

    const usersEmails: string[] = [];

    this.chat.agents.forEach((agent) => {
      if (usersEmails.indexOf(agent.login) === -1) {
        usersEmails.push(agent.login);
      }
    });

    this.chat.supervisors.forEach((supervisor) => {
      if (usersEmails.indexOf(supervisor.login) === -1) {
        usersEmails.push(supervisor.login);
      }
    });


    const users = await this.userRepository.getUsersByEmails(usersEmails);

    const usersProfiles = await this.profileRepository
      .getBaseProfileDataByUsersIds(users.map((user) => String(user.id)));

    users.forEach((user) => {
      const profileIndex = usersProfiles.findIndex((profile) => profile.userId === String(user.id));
      if (profileIndex === -1) {
        return;
      }

      const agentIndex = this.chat.agents.findIndex((agent) => agent.login === user.email);
      if (agentIndex !== -1) {
        this.chat.agents[agentIndex].id = user.id;
        this.chat.agents[agentIndex].fullName =
          `${usersProfiles[profileIndex].firstName} ${usersProfiles[profileIndex].lastName}`;
      }

      const supervisorIndex = this.chat.supervisors.findIndex((supervisor) => supervisor.login === user.email);
      if (supervisorIndex !== -1) {
        this.chat.supervisors[supervisorIndex].id = user.id;
        this.chat.supervisors[supervisorIndex].fullName =
          `${usersProfiles[profileIndex].firstName} ${usersProfiles[profileIndex].lastName}`;
      }
    });

    this.chat.messages.sort((messageA, messageB) => messageA.timestamp - messageB.timestamp);
    this.chat.comments.sort((commentA, commentB) => commentA.comment.timestamp - commentB.comment.timestamp);
    this.loading = false;
  }


  private async deleteComment(commentId: number): Promise<void> {
    this.commentIdForDialog = commentId;
    this.deleteCommentDialog = 'delete-comment-dialog';
  }

  private async deleteNote(noteId: number): Promise<void> {
    this.noteIdForDialog = noteId;
    this.deleteNoteDialog = 'delete-note-dialog';
  }

  private removeNote(noteId: number): void {
    const noteIndex = this.chat.notes.findIndex((chatNote) => chatNote.note.id === noteId);

    if (noteIndex !== -1) {
      delete this.chat.notes[noteIndex];
      this.chat.notes = this.chat.notes.filter((note) => !!note);
    }

    this.$forceUpdate();
  }

  private removeComment(commentId: number): void {
    let commentIndex = this.chat.comments.findIndex((chatComment) => chatComment.comment.id === commentId);

    if (commentIndex !== -1) {
      delete this.chat.comments[commentIndex];
      this.chat.comments = this.chat.comments.filter((comment) => !!comment);
    } else {
      let messageIndex = -1;
      this.chat.messages.forEach((message, mi) => {
        message.comments.forEach((messageComment, ci) => {
          if (messageComment.comment.id === commentId) {
            messageIndex = mi;
            commentIndex = ci;
          }
        });

      });

      delete this.chat.messages[messageIndex].comments[commentIndex];
      this.chat.messages[messageIndex].comments = this.chat.messages[messageIndex]
        .comments.filter((comment) => !!comment);
    }

    this.$forceUpdate();
  }

  private async getUsersInfo(): Promise<void> {
    const usersIds: string[] = [];
    this.chat.comments.forEach((chatComment) => {
      if (usersIds.indexOf(chatComment.comment.authorId) === -1) {
        usersIds.push(chatComment.comment.authorId);
      }
    });

    if (this.chat.notes) {
      this.chat.notes.forEach((chatNote) => {
        if (usersIds.indexOf(chatNote.note.authorId) === -1) {
          usersIds.push(chatNote.note.authorId);
        }
      });
    }

    this.chat.messages.forEach((message) => {
      message.comments.forEach((messageComment) => {
        if (usersIds.indexOf(messageComment.comment.authorId) === -1) {
          usersIds.push(messageComment.comment.authorId);
        }
      });
    });

    if (usersIds.indexOf(this.chat.reviewerId) === -1) {
      usersIds.push(this.chat.reviewerId);
    }

    if (usersIds.indexOf(this.feedback.feedback.feedbackFromUserId) === -1) {
      usersIds.push(this.feedback.feedback.feedbackFromUserId);
    }

    if (usersIds.indexOf(this.feedback.feedback.feedbackToUserId) === -1) {
      usersIds.push(this.feedback.feedback.feedbackToUserId);
    }

    const users = await this.userRepository.getUsersByIds(usersIds);

    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });
    this.$forceUpdate();
  }
}
